/**
 * AYARLAR › ABONELİK — kasanın ücretli kabiliyetleri ve aktivasyon kodu.
 *
 * Kilitli kabiliyet bir anormallik değildir (CLAUDE.md §6): bu kartta uyarı
 * işareti ve yargı rengi yok; hâller düz cümleyle söylenir.
 */

import { useEffect, useRef, useState, type FormEvent } from "react";
import { routeSorgusunuAl } from "../lib/router";
import { KABILIYETLER, type KabiliyetAdi } from "@parafon/contracts/sertifika";
import { Button, Field, TextInput } from "../ui/primitives";
import { kapiHataKodu, useKabiliyet } from "./KabiliyetSaglayici";
import type { KabiliyetDurumu } from "./kabiliyet";
import { M, YEREL } from "../dil";

function gunGoster(iso: string): string {
  return iso.split("-").reverse().join(".");
}

function anGoster(iso: string): string {
  return new Date(iso).toLocaleString(YEREL, {
    timeZone: "Europe/Istanbul",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Açık kabiliyetlerin ekrandaki adları; sayılı olanlarda adet. */
export function kabiliyetEtiketleri(d: KabiliyetDurumu): string[] {
  const k = d.kabiliyetler;
  return KABILIYETLER.filter((x) => x.ad in k).map((x) => {
    const adet = k[x.ad as KabiliyetAdi];
    const ad = M.ayarlar.kabiliyetAdi[x.ad];
    return x.tur === "sayi" && typeof adet === "number" && adet > 1 ? `${ad} ×${adet}` : ad;
  });
}

function halCumlesi(d: KabiliyetDurumu): string {
  switch (d.hal) {
    case "yukleniyor":
      return M.ayarlar.abonelik.hal.yukleniyor;
    case "sertifikasiz":
      return M.ayarlar.abonelik.hal.sertifikasiz;
    case "kapi-yok":
      return M.ayarlar.abonelik.hal.kapiYok;
    case "sunucu":
      return d.bitis === null ? M.ayarlar.abonelik.hal.suresiz : M.ayarlar.abonelik.hal.tarihineKadar(gunGoster(d.bitis));
    case "son-teyit":
      return M.ayarlar.abonelik.hal.sonTeyit(d.sonTeyit === null ? "—" : anGoster(d.sonTeyit));
    case "teyit-eskidi":
      return M.ayarlar.abonelik.hal.teyitEskidi;
    case "suresi-doldu":
      return M.ayarlar.abonelik.hal.suresiDoldu;
    case "baska-cihazda":
      return M.ayarlar.abonelik.hal.baskaCihazda;
    case "gecersiz":
      return M.ayarlar.abonelik.hal.gecersiz;
  }
}

export function kodHataMesaji(err: unknown): string {
  switch (kapiHataKodu(err)) {
    case "KOD_YOK":
      return M.ayarlar.abonelik.hata.kodYok;
    case "GECERSIZ":
      return M.ayarlar.abonelik.hata.gecersiz;
    case "MUHUR":
      return M.ayarlar.abonelik.hata.muhur;
    case "RATE_LIMITED":
      return M.ayarlar.abonelik.hata.cokDeneme;
    default:
      return err instanceof Error && kapiHataKodu(err) === null && err.message !== ""
        ? err.message
        : M.ayarlar.abonelik.hata.ulasilamadi;
  }
}

export function AbonelikKarti() {
  const { durum, eskisiDustu, kodKullan, yenidenAc } = useKabiliyet();
  const [kod, setKod] = useState("");
  const [mesgul, setMesgul] = useState(false);
  const [hata, setHata] = useState<string | null>(null);
  const [tamam, setTamam] = useState<string | null>(null);

  const gonder = async (e: FormEvent) => {
    e.preventDefault();
    setHata(null);
    setTamam(null);
    setMesgul(true);
    try {
      const g = await kodKullan(kod);
      const adlar = g.tur === "kurucu" ? [M.ayarlar.abonelik.butunKabiliyetler] : g.kabiliyetler.map((k) => (M.ayarlar.kabiliyetAdi as Readonly<Record<string, string>>)[k.ad] ?? k.ad);
      setTamam(M.ayarlar.abonelik.kodKullanildi(adlar.join(", ")));
      setKod("");
    } catch (err) {
      setHata(kodHataMesaji(err));
    } finally {
      setMesgul(false);
    }
  };

  // Kilit notlarındaki "Abonelik" bağlantısı buraya kaydırır.
  const bolum = useRef<HTMLElement>(null);
  useEffect(() => {
    if (routeSorgusunuAl("bolum") === "abonelik") bolum.current?.scrollIntoView?.({ block: "start" });
  }, []);

  const etiketler = kabiliyetEtiketleri(durum);
  return (
    <section ref={bolum} id="abonelik" data-kart-bolum="abonelik" className="pf-card scroll-mt-14 p-4" aria-label={M.ayarlar.abonelik.baslik}>
      <h2 className="text-[14px] font-semibold text-ink">{M.ayarlar.abonelik.baslik}</h2>
      <p className="mt-1 text-[13px] text-ink">{halCumlesi(durum)}</p>
      {etiketler.length > 0 && (
        <ul aria-label={M.ayarlar.abonelik.acikKabiliyetler} className="mt-2 list-disc pl-5 text-[12px] text-muted">
          {etiketler.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      )}
      {eskisiDustu && (
        <p className="mt-2 text-[12px] text-muted">{M.ayarlar.abonelik.eskisiDustu}</p>
      )}
      {durum.hal === "baska-cihazda" && (
        <div className="mt-2">
          <Button onClick={() => void yenidenAc()}>{M.ayarlar.abonelik.yenidenAc}</Button>
          <p className="mt-1 text-[12px] text-muted">{M.ayarlar.abonelik.sinirDoluysa}</p>
        </div>
      )}
      {durum.hal !== "kapi-yok" && (
        <form className="mt-3 flex flex-col gap-2 sm:w-96" onSubmit={(e) => void gonder(e)}>
          <Field label={M.ayarlar.abonelik.kod} htmlFor="abonelik-kod" {...(hata === null ? {} : { error: hata })}>
            <TextInput
              id="abonelik-kod"
              value={kod}
              placeholder="XXXX-XXXX-XXXX-XXXX"
              onChange={(e) => setKod(e.currentTarget.value)}
            />
          </Field>
          <div>
            <Button type="submit" variant="primary" disabled={mesgul || kod.trim() === ""}>
              {mesgul ? M.ayarlar.abonelik.gonderiliyor : M.ayarlar.abonelik.kodKullan}
            </Button>
          </div>
          {tamam !== null && (
            <p role="status" className="text-[12px] text-ink">
              {tamam}
            </p>
          )}
        </form>
      )}
    </section>
  );
}
